<template>
  <div
    ref="containerRef"
    class="canvas-container"
    :class="{
      'is-dragging-over': isDraggingOver,
      'is-edit-mode': !readonly,
      'is-drawing': isDrawingMode && !readonly,
      'is-space-pan': isSpaceHeld && !isDrawingMode,
      'is-endpoint-drag': !!draggingEndpoint,
      'is-segment-drag': !!draggingSegment,
      'is-selecting': !!selectionBoxRect,
    }"
    @wheel.prevent="onWheel"
    @mousedown="onMouseDown"
    @mousemove="onMouseMove"
    @mouseup="onMouseUp"
    @mouseleave="onMouseLeave"
    @click="onCanvasClick"
    @dragover.prevent="!readonly && onDragOver()"
    @dragleave="!readonly && onDragLeave($event)"
    @drop.prevent="!readonly && onDrop($event)"
  >
    <!-- Zoomable / pannable content -->
    <div class="canvas-content" :style="contentStyle">
      <!-- Base SVG: rendered from the base64 data URI supplied by the API -->
      <img
        :src="baseLayout"
        class="base-svg"
        alt="Floor plan"
        draggable="false"
        @load="onImageLoad"
      />

      <!-- Overlay layer – absolutely covers the base SVG -->
      <div ref="overlayRef" class="overlay-layer">
        <!-- Segment lines layer -->
        <svg
          v-if="svgW && svgH"
          class="connections-svg"
          :class="{ 'draw-mode': isDrawingMode && !readonly }"
          :width="svgW"
          :height="svgH"
          xmlns="http://www.w3.org/2000/svg"
          @mousedown="onSvgMouseDown"
        >
          <defs>
            <marker
              v-for="color in CONNECTION_COLORS"
              :key="color"
              :id="`dt-arrow-${color.slice(1)}`"
              markerWidth="18"
              markerHeight="17"
              refX="3"
              refY="8.5"
              orient="auto"
              markerUnits="userSpaceOnUse"
            >
              <polygon points="0 0, 18 8.5, 0 17" :fill="color" />
            </marker>
          </defs>

          <!-- Existing segments -->
          <line
            v-for="seg in segments"
            :key="seg.id"
            :x1="(seg.from.x / 100) * svgW"
            :y1="(seg.from.y / 100) * svgH"
            :x2="(seg.to.x / 100) * svgW"
            :y2="(seg.to.y / 100) * svgH"
            :stroke="seg.color"
            :stroke-width="selectedSegmentId === seg.id ? 10 : 7"
            stroke-linecap="square"
            :pathLength="animatingSegmentIds.includes(seg.id) ? '1' : undefined"
            :stroke-dasharray="
              animatingSegmentIds.includes(seg.id)
                ? '1 0'
                : seg.style === 'dashed'
                  ? '18 12'
                  : undefined
            "
            :marker-end="
              seg.hasArrow ? `url(#dt-arrow-${seg.color.slice(1)})` : undefined
            "
            class="connection-line"
            :class="{
              'connection-line--animating': animatingSegmentIds.includes(
                seg.id
              ),
              'connection-line--selected': selectedSegmentId === seg.id,
            }"
            @mousedown.stop="onSegmentMouseDown($event, seg.id)"
            @click.stop="selectedSegmentId = seg.id"
          />

          <!-- Endpoint handles (visible in edit mode; draggable to adjust/connect) -->
          <template v-if="!readonly">
            <g v-for="seg in segments" :key="`h-${seg.id}`">
              <circle
                class="endpoint-handle"
                :r="selectedSegmentId === seg.id ? 7 : 5"
                :cx="(seg.from.x / 100) * svgW"
                :cy="(seg.from.y / 100) * svgH"
                fill="white"
                :stroke="seg.color"
                stroke-width="2.5"
                fill-opacity="0.92"
                @mousedown.stop="onHandleMouseDown($event, seg.id, 'from')"
              />
              <circle
                class="endpoint-handle"
                :r="selectedSegmentId === seg.id ? 7 : 5"
                :cx="(seg.to.x / 100) * svgW"
                :cy="(seg.to.y / 100) * svgH"
                fill="white"
                :stroke="seg.color"
                stroke-width="2.5"
                fill-opacity="0.92"
                @mousedown.stop="onHandleMouseDown($event, seg.id, 'to')"
              />
            </g>
          </template>

          <!-- Endpoint snap indicator -->
          <circle
            v-if="snapTarget"
            :cx="(snapTarget.x / 100) * svgW"
            :cy="(snapTarget.y / 100) * svgH"
            r="8"
            :fill="newLineColor"
            fill-opacity="0.25"
            :stroke="newLineColor"
            stroke-width="2"
            pointer-events="none"
          />

          <!-- Ortho: placed anchor dot (step ≥ 1) -->
          <circle
            v-if="is90DegreeMode && orthoStep >= 1 && orthoAnchor"
            :cx="(orthoAnchor.x / 100) * svgW"
            :cy="(orthoAnchor.y / 100) * svgH"
            r="5"
            :fill="newLineColor"
            fill-opacity="0.9"
            pointer-events="none"
          />

          <!-- Ortho: placed elbow dot (step 2) -->
          <circle
            v-if="is90DegreeMode && orthoStep === 2 && orthoMid"
            :cx="(orthoMid.x / 100) * svgW"
            :cy="(orthoMid.y / 100) * svgH"
            r="5"
            :fill="newLineColor"
            fill-opacity="0.9"
            pointer-events="none"
          />

          <!-- Live draw preview -->
          <line
            v-if="drawStart && drawCurrent"
            :x1="(drawStart.x / 100) * svgW"
            :y1="(drawStart.y / 100) * svgH"
            :x2="(drawCurrent.x / 100) * svgW"
            :y2="(drawCurrent.y / 100) * svgH"
            :stroke="newLineColor"
            stroke-width="5"
            stroke-linecap="butt"
            stroke-opacity="0.5"
            stroke-dasharray="6 4"
            :marker-end="
              newLineHasArrow
                ? `url(#dt-arrow-${newLineColor.slice(1)})`
                : undefined
            "
            pointer-events="none"
          />
        </svg>

        <!-- Color picker + delete for the selected segment -->
        <div
          v-if="selectedSegment"
          class="connection-color-picker"
          :style="{ left: `${segmentMidPct.x}%`, top: `${segmentMidPct.y}%` }"
          @mousedown.stop
          @click.stop
        >
          <button
            v-for="color in CONNECTION_COLORS"
            :key="color"
            class="color-swatch"
            :style="{ background: color }"
            :class="{ 'color-swatch--active': selectedSegment.color === color }"
            :title="color"
            @click.stop="setSegmentColor(color)"
          />
          <button
            class="color-swatch arrow-toggle-btn"
            :title="selectedSegment.hasArrow ? 'Remove arrow' : 'Add arrow'"
            @click.stop="setActiveArrow(!selectedSegment.hasArrow)"
          >
            <v-icon size="12" color="white">
              {{
                selectedSegment.hasArrow
                  ? 'mdi-arrow-right-bold'
                  : 'mdi-arrow-right'
              }}
            </v-icon>
          </button>
          <button
            class="color-swatch segment-delete-btn"
            title="Delete line (Del)"
            @click.stop="deleteSelectedSegment"
          >
            <v-icon size="12" color="white">mdi-delete</v-icon>
          </button>
        </div>

        <OverlayItem
          v-for="item in overlay"
          :key="item.id"
          :item="item"
          :canvasRef="overlayRef"
          :selected="(selectedIds ?? []).includes(item.id)"
          :readonly="readonly"
          :readings="equipmentReadingsMap?.[item.itemId]"
          :iconSize="iconSize"
          @remove="(id) => emit('remove', id)"
          @move="onItemMove"
          @moveStart="(id) => emit('moveStart', id)"
          @moveEnd="onItemMoveEnd"
          @select="(id, multi) => emit('select', id, multi)"
          @resizeStart="(id) => emit('resizeStart', id)"
          @resize="(p) => emit('resize', p.id, p.scale)"
        />
        <!-- Snap-to-alignment guide lines (visible while dragging an item) -->
        <div
          v-if="snapGuides.x !== null"
          class="snap-guide snap-guide--vertical"
          :style="{ left: `${snapGuides.x}%` }"
        />
        <div
          v-if="snapGuides.y !== null"
          class="snap-guide snap-guide--horizontal"
          :style="{ top: `${snapGuides.y}%` }"
        />
      </div>
    </div>

    <!-- Line editor toolbox -->
    <div v-if="!readonly" class="line-toolbox" @mousedown.stop @click.stop>
      <v-btn
        class="toolbox-btn"
        density="compact"
        icon
        :color="isDrawingMode ? 'primary' : 'default'"
        :title="isDrawingMode ? 'Drawing mode: ON (Esc to exit)' : 'Draw lines'"
        @click="isDrawingMode = !isDrawingMode"
      >
        <v-icon>mdi-pencil-ruler</v-icon>
      </v-btn>

      <v-divider class="toolbox-divider" />

      <!-- Line style -->
      <v-btn
        class="toolbox-btn"
        density="compact"
        icon
        :color="activeLineStyle === 'solid' ? 'primary' : 'default'"
        title="Solid line"
        @click="setActiveStyle('solid')"
      >
        <v-icon>mdi-minus-thick</v-icon>
      </v-btn>
      <v-btn
        class="toolbox-btn"
        density="compact"
        icon
        :color="activeLineStyle === 'dashed' ? 'primary' : 'default'"
        title="Dashed line"
        @click="setActiveStyle('dashed')"
      >
        <v-icon>mdi-dots-horizontal</v-icon>
      </v-btn>

      <v-divider class="toolbox-divider" />

      <!-- 90-degree mode -->
      <v-btn
        class="toolbox-btn"
        density="compact"
        icon
        :color="is90DegreeMode ? 'primary' : 'default'"
        :title="
          is90DegreeMode
            ? `Orthogonal mode: ON (step ${orthoStep}/2, Esc to cancel)`
            : 'Orthogonal mode: click-click-click L-shape'
        "
        @click="toggleOrthoMode"
      >
        <v-icon>mdi-angle-right</v-icon>
      </v-btn>

      <v-divider class="toolbox-divider" />

      <!-- Arrow toggle -->
      <v-btn
        class="toolbox-btn"
        density="compact"
        icon
        :color="activeLineHasArrow ? 'primary' : 'default'"
        :title="activeLineHasArrow ? 'Arrow: ON' : 'Arrow: OFF'"
        @click="setActiveArrow(!activeLineHasArrow)"
      >
        <v-icon>mdi-arrow-right-bold</v-icon>
      </v-btn>

      <v-divider class="toolbox-divider" />

      <!-- Color swatches -->
      <div class="color-swatch-grid">
        <button
          v-for="color in CONNECTION_COLORS"
          :key="color"
          class="color-swatch"
          :style="{ background: color }"
          :class="{ 'color-swatch--active': activeLineColor === color }"
          :title="color"
          @click="setActiveColor(color)"
        />
      </div>
    </div>

    <!-- Zoom controls -->
    <div class="canvas-controls">
      <v-btn
        size="large"
        density="compact"
        :disabled="scale >= MAX_SCALE"
        icon
        title="Zoom in"
        @click="zoomIn"
      >
        <v-icon color="primary">mdi-plus-thick</v-icon>
      </v-btn>
      <v-btn
        size="large"
        density="compact"
        :disabled="scale <= MIN_SCALE"
        icon
        title="Zoom out"
        @click="zoomOut"
      >
        <v-icon color="primary">mdi-minus-thick</v-icon>
      </v-btn>
      <v-btn
        size="large"
        density="compact"
        :disabled="!canReset"
        icon
        title="Reset view"
        @click="reset"
      >
        <v-icon color="primary">mdi-magnify-remove-cursor</v-icon>
      </v-btn>
    </div>

    <!-- Rubber-band selection box -->
    <div
      v-if="selectionBoxRect"
      class="selection-box"
      :style="{
        left: `${selectionBoxRect.left}px`,
        top: `${selectionBoxRect.top}px`,
        width: `${selectionBoxRect.width}px`,
        height: `${selectionBoxRect.height}px`,
      }"
    />

    <!-- Contextual / rotating hint -->
    <div v-if="!readonly" class="hint-container">
      <HintBox :hint="currentHint" :visible="hintsEnabled" @toggle="hintsEnabled = !hintsEnabled" />
    </div>

    <!-- Drop hint overlay -->
    <Transition name="fade">
      <div v-if="isDraggingOver" class="drop-hint">
        <v-icon color="primary" size="32" class="mb-1"
          >mdi-map-marker-plus-outline</v-icon
        >
        <span>Drop to place on map</span>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue';

import OverlayItem from './OverlayItem.vue';
import HintBox from './HintBox.vue';

import type { IOverlayItem, ISegment, IEquipment, IEquipmentReading } from '../types';

const CONNECTION_COLORS = [
  '#1565c0',
  '#d32f2f',
  '#2e7d32',
  '#e65100',
  '#6a1b9a',
  '#00838f',
  '#37474f',
];

// ─────────────────────────────────────────────────────────────────────────────
// Props & Emits
// ─────────────────────────────────────────────────────────────────────────────
const props = defineProps<{
  baseLayout: string;
  overlay: IOverlayItem[];
  segments: ISegment[];
  /** UUIDs of items selected individually (blue ring). */
  selectedIds?: string[];
  /** When true, disables drag-and-drop onto the canvas (read-only viewer). */
  readonly?: boolean;
  /** Latest readings keyed by equipmentId — shown as stacked chips when readonly. */
  equipmentReadingsMap?: Record<number, IEquipmentReading[]>;
}>();

const emit = defineEmits<{
  remove: [id: string];
  /** id, x%, y% */
  move: [id: string, x: number, y: number];
  /** Fired once when item drag begins – before any position change. */
  moveStart: [id: string];
  /** id = item UUID (null = deselect all); multi = Ctrl/Cmd held */
  select: [id: string | null, multi: boolean];
  /** Fired when a drag-selection box is released — carries all item ids inside the rect. */
  selectMultiple: [ids: string[]];
  segmentsChange: [segments: ISegment[]];
  /** Fired once when resize drag begins – before any scale change. */
  resizeStart: [id: string];
  /** id, new scale multiplier */
  resize: [id: string, scale: number];
  drop: [
    payload: {
      equipment: IEquipment;
      siteSystemId?: number;
      siteSystemName?: string;
      /** Present when dropped from the peer-picker modal — readings already known. */
      readings?: IEquipmentReading[];
      x: number;
      y: number;
    },
  ];
}>();

// ─── Segment state ───────────────────────────────────────────────────────────
const segments = ref<ISegment[]>([...props.segments]);
const selectedSegmentId = ref<string | null>(null);
const animatingSegmentIds = ref<string[]>([]);

// Drawing tool
const isDrawingMode = ref(false);
/** When true, click-click-click mode: A → B free, B → C forced perpendicular to A→B. */
const is90DegreeMode = ref(false);
const drawStart = ref<{ x: number; y: number } | null>(null);
const drawCurrent = ref<{ x: number; y: number } | null>(null);
/** Endpoint being snapped to — used to render the snap indicator. */
const snapTarget = ref<{ x: number; y: number } | null>(null);
const newLineStyle = ref<'solid' | 'dashed'>('solid');
const newLineHasArrow = ref(true);
const newLineColor = ref(CONNECTION_COLORS[0]);

// Orthogonal multi-click state (0 = idle, 1 = anchor placed, 2 = elbow placed)
const orthoStep = ref<0 | 1 | 2>(0);
const orthoAnchor = ref<{ x: number; y: number } | null>(null);
const orthoMid = ref<{ x: number; y: number } | null>(null);

/** Which segment endpoint is currently being dragged, if any. */
const draggingEndpoint = ref<{ segmentId: string; end: 'from' | 'to' } | null>(
  null
);

/** State for dragging the entire segment body (and connected group). */
const draggingSegment = ref<{
  startMousePct: { x: number; y: number };
  groupIds: string[];
  originalPositions: Map<
    string,
    { from: { x: number; y: number }; to: { x: number; y: number } }
  >;
} | null>(null);

const ENDPOINT_SNAP_THRESHOLD = 2.5; // % units
const ANGLE_SNAP_THRESHOLD = 12; // degrees — snap zone around each standard angle

/** Pixel dimensions of the overlay layer — kept in sync via ResizeObserver. */
const svgW = ref(0);
const svgH = ref(0);

// ─── Computed ────────────────────────────────────────────────────────────────

/** Icon size in px — 8% of canvas width, clamped between 48px and 160px. */
const iconSize = computed(() =>
  Math.max(48, Math.min(160, svgW.value * 0.08))
);

const selectedSegment = computed(
  () => segments.value.find((s) => s.id === selectedSegmentId.value) ?? null
);

const segmentMidPct = computed(() => {
  if (!selectedSegment.value) return { x: 50, y: 50 };
  const { from, to } = selectedSegment.value;
  return { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 };
});

// ─── Active-line properties (reflect selected segment or new-line defaults) ───
const activeLineStyle = computed<'solid' | 'dashed'>(
  () => selectedSegment.value?.style ?? newLineStyle.value
);
const activeLineHasArrow = computed<boolean>(
  () => selectedSegment.value?.hasArrow ?? newLineHasArrow.value
);
const activeLineColor = computed<string>(
  () => selectedSegment.value?.color ?? newLineColor.value
);

const setActiveStyle = (style: 'solid' | 'dashed'): void => {
  if (selectedSegment.value) selectedSegment.value.style = style;
  else newLineStyle.value = style;
};
const setActiveArrow = (v: boolean): void => {
  if (selectedSegment.value) selectedSegment.value.hasArrow = v;
  else newLineHasArrow.value = v;
};
const setActiveColor = (color: string): void => {
  if (selectedSegment.value) selectedSegment.value.color = color;
  else newLineColor.value = color;
};

// ─── Segment helpers ─────────────────────────────────────────────────────────
const setSegmentColor = (color: string): void => {
  if (selectedSegment.value) selectedSegment.value.color = color;
};

const deleteSelectedSegment = (): void => {
  if (!selectedSegmentId.value) return;
  segments.value = segments.value.filter(
    (s) => s.id !== selectedSegmentId.value
  );
  selectedSegmentId.value = null;
};

const animateSegment = (id: string): void => {
  animatingSegmentIds.value.push(id);
  setTimeout(() => {
    animatingSegmentIds.value = animatingSegmentIds.value.filter(
      (i) => i !== id
    );
  }, 750);
};

/** Converts a MouseEvent to % coords relative to the overlay layer. */
const toOverlayPct = (e: MouseEvent): { x: number; y: number } | null => {
  if (!overlayRef.value) return null;
  const rect = overlayRef.value.getBoundingClientRect();
  return {
    x: Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100)),
    y: Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100)),
  };
};

// ─── Snap helpers ────────────────────────────────────────────────────────────
/** Snaps pt to the nearest existing segment endpoint within ENDPOINT_SNAP_THRESHOLD. */
const snapToEndpoints = (
  pt: { x: number; y: number },
  excludeSegmentId?: string
): { x: number; y: number } => {
  let nearest: { x: number; y: number } | null = null;
  let minDist = ENDPOINT_SNAP_THRESHOLD;
  for (const seg of segments.value) {
    if (seg.id === excludeSegmentId) continue;
    for (const ep of [seg.from, seg.to]) {
      const dist = Math.sqrt((pt.x - ep.x) ** 2 + (pt.y - ep.y) ** 2);
      if (dist < minDist) {
        minDist = dist;
        nearest = ep;
      }
    }
  }
  snapTarget.value = nearest;
  return nearest ?? pt;
};

/**
 * Projects `to` onto the nearest 45° increment angle from `from`.
 * Only snaps within ANGLE_SNAP_THRESHOLD degrees; outside that the cursor is free.
 * Uses pixel coords so projected angles are visually correct.
 */
const applyAngleSnap = (
  from: { x: number; y: number },
  to: { x: number; y: number }
): { x: number; y: number } => {
  if (!svgW.value || !svgH.value) return to;
  const SNAP_ANGLES = [0, 45, 90, 135, 180, 225, 270, 315];
  const fx = (from.x / 100) * svgW.value;
  const fy = (from.y / 100) * svgH.value;
  const tx = (to.x / 100) * svgW.value;
  const ty = (to.y / 100) * svgH.value;
  const dx = tx - fx;
  const dy = ty - fy;
  const angleDeg = Math.atan2(dy, dx) * (180 / Math.PI);
  const lengthPx = Math.sqrt(dx * dx + dy * dy);
  if (lengthPx < 1) return to;

  let bestAngle: number | null = null;
  let minDiff = ANGLE_SNAP_THRESHOLD;
  for (const a of SNAP_ANGLES) {
    const diff = Math.abs(((angleDeg - a + 540) % 360) - 180);
    if (diff < minDiff) {
      minDiff = diff;
      bestAngle = a;
    }
  }
  if (bestAngle === null) return to;

  const rad = bestAngle * (Math.PI / 180);
  return {
    x: ((fx + lengthPx * Math.cos(rad)) / svgW.value) * 100,
    y: ((fy + lengthPx * Math.sin(rad)) / svgH.value) * 100,
  };
};

/**
 * Projects cursor onto the line through orthoMid that is perpendicular to
 * the first segment (orthoAnchor → orthoMid). Used for the 3rd click in
 * the orthogonal state machine.
 */
const getOrthoPerpPoint = (cursor: {
  x: number;
  y: number;
}): { x: number; y: number } => {
  if (!orthoAnchor.value || !orthoMid.value || !svgW.value || !svgH.value)
    return cursor;
  const ax = (orthoAnchor.value.x / 100) * svgW.value;
  const ay = (orthoAnchor.value.y / 100) * svgH.value;
  const bx = (orthoMid.value.x / 100) * svgW.value;
  const by = (orthoMid.value.y / 100) * svgH.value;
  const d1x = bx - ax;
  const d1y = by - ay;
  // Perpendicular direction to A→B: (-d1y, d1x)
  const perpX = -d1y;
  const perpY = d1x;
  const dot = perpX * perpX + perpY * perpY;
  if (dot < 0.0001) return cursor;
  const px = (cursor.x / 100) * svgW.value;
  const py = (cursor.y / 100) * svgH.value;
  const t = ((px - bx) * perpX + (py - by) * perpY) / dot;
  return {
    x: ((bx + t * perpX) / svgW.value) * 100,
    y: ((by + t * perpY) / svgH.value) * 100,
  };
};

const resetOrthoState = (): void => {
  drawStart.value = null;
  drawCurrent.value = null;
  snapTarget.value = null;
  orthoStep.value = 0;
  orthoAnchor.value = null;
  orthoMid.value = null;
};

const toggleOrthoMode = (): void => {
  is90DegreeMode.value = !is90DegreeMode.value;
  if (!is90DegreeMode.value) resetOrthoState();
};

// ─── Drawing handlers ────────────────────────────────────────────────────────

/**
 * Orthogonal state machine: 3 clicks produce an L-shaped pair of segments.
 * Step 0→1: place anchor A (no segment yet).
 * Step 1→2: place elbow B, draw A→B (free direction with 45° snap).
 * Step 2→0: place end C forced perpendicular to A→B, draw B→C.
 */
const handleOrthoClick = (e: MouseEvent): void => {
  if (e.button !== 0) return;
  if ((e.target as HTMLElement).closest('.overlay-item')) return;
  const raw = toOverlayPct(e);
  if (!raw) return;

  if (orthoStep.value === 0) {
    const pt = snapToEndpoints(raw);
    orthoAnchor.value = pt;
    drawStart.value = pt;
    orthoStep.value = 1;
    snapTarget.value = null;
  } else if (orthoStep.value === 1 && drawStart.value) {
    const snapped = snapToEndpoints(raw);
    const pt = snapTarget.value
      ? snapped
      : applyAngleSnap(drawStart.value, snapped);
    const dx = pt.x - drawStart.value.x;
    const dy = pt.y - drawStart.value.y;
    if (Math.sqrt(dx * dx + dy * dy) > 0.5) {
      orthoMid.value = pt;
      const id = crypto.randomUUID();
      segments.value.push({
        id,
        from: { ...drawStart.value },
        to: pt,
        color: newLineColor.value,
        style: newLineStyle.value,
        hasArrow: false, // first leg has no arrowhead; the second leg carries it
      });
      animateSegment(id);
      drawStart.value = pt;
      orthoStep.value = 2;
      snapTarget.value = null;
    }
  } else if (orthoStep.value === 2 && drawStart.value) {
    const snapped = snapToEndpoints(raw);
    const pt = snapTarget.value ? snapped : getOrthoPerpPoint(raw);
    const dx = pt.x - drawStart.value.x;
    const dy = pt.y - drawStart.value.y;
    if (Math.sqrt(dx * dx + dy * dy) > 0.5) {
      const id = crypto.randomUUID();
      segments.value.push({
        id,
        from: { ...drawStart.value },
        to: pt,
        color: newLineColor.value,
        style: newLineStyle.value,
        hasArrow: newLineHasArrow.value,
      });
      animateSegment(id);
    }
    resetOrthoState();
  }
};

// ─── Segment body drag (move whole segment + connected group) ────────────────
const sharesEndpoint = (a: ISegment, b: ISegment): boolean => {
  const eps = 0.05;
  const eq = (p: { x: number; y: number }, q: { x: number; y: number }) =>
    Math.abs(p.x - q.x) < eps && Math.abs(p.y - q.y) < eps;
  return (
    eq(a.from, b.from) || eq(a.from, b.to) || eq(a.to, b.from) || eq(a.to, b.to)
  );
};

const getConnectedGroup = (segmentId: string): string[] => {
  const visited = new Set<string>([segmentId]);
  const queue = [segmentId];
  while (queue.length) {
    const currentId = queue.shift()!;
    const current = segments.value.find((s) => s.id === currentId);
    if (!current) continue;
    for (const seg of segments.value) {
      if (visited.has(seg.id)) continue;
      if (sharesEndpoint(current, seg)) {
        visited.add(seg.id);
        queue.push(seg.id);
      }
    }
  }
  return [...visited];
};

const onSegmentMouseDown = (e: MouseEvent, segmentId: string): void => {
  if (isDrawingMode.value || props.readonly) return;
  e.stopPropagation();
  e.preventDefault();
  const raw = toOverlayPct(e);
  if (!raw) return;
  const groupIds = getConnectedGroup(segmentId);
  const originalPositions = new Map<
    string,
    { from: { x: number; y: number }; to: { x: number; y: number } }
  >();
  for (const id of groupIds) {
    const seg = segments.value.find((s) => s.id === id);
    if (seg)
      originalPositions.set(id, { from: { ...seg.from }, to: { ...seg.to } });
  }
  draggingSegment.value = { startMousePct: raw, groupIds, originalPositions };
};

const onSegmentDrag = (e: MouseEvent): void => {
  if (!draggingSegment.value) return;
  const raw = toOverlayPct(e);
  if (!raw) return;
  const dx = raw.x - draggingSegment.value.startMousePct.x;
  const dy = raw.y - draggingSegment.value.startMousePct.y;
  for (const id of draggingSegment.value.groupIds) {
    const seg = segments.value.find((s) => s.id === id);
    const orig = draggingSegment.value.originalPositions.get(id);
    if (!seg || !orig) continue;
    seg.from = {
      x: Math.max(0, Math.min(100, orig.from.x + dx)),
      y: Math.max(0, Math.min(100, orig.from.y + dy)),
    };
    seg.to = {
      x: Math.max(0, Math.min(100, orig.to.x + dx)),
      y: Math.max(0, Math.min(100, orig.to.y + dy)),
    };
  }
};

// ─── Endpoint drag handlers ──────────────────────────────────────────────────
const onHandleMouseDown = (
  e: MouseEvent,
  segmentId: string,
  end: 'from' | 'to'
): void => {
  e.stopPropagation();
  e.preventDefault();
  selectedSegmentId.value = segmentId;
  draggingEndpoint.value = { segmentId, end };
};

const onEndpointDrag = (e: MouseEvent): void => {
  if (!draggingEndpoint.value) return;
  const { segmentId, end } = draggingEndpoint.value;
  const seg = segments.value.find((s) => s.id === segmentId);
  if (!seg) return;
  const raw = toOverlayPct(e);
  if (!raw) return;
  const snapped = snapToEndpoints(raw, segmentId);
  const fixedPt = end === 'from' ? seg.to : seg.from;
  seg[end] = snapTarget.value ? snapped : applyAngleSnap(fixedPt, snapped);
};

const onEndpointDragEnd = (e: MouseEvent): void => {
  if (!draggingEndpoint.value) return;
  onEndpointDrag(e); // apply final snapped position
  draggingEndpoint.value = null;
  snapTarget.value = null;
};

const onSvgMouseDown = (e: MouseEvent): void => {
  if (!isDrawingMode.value || props.readonly) return;
  e.stopPropagation();
  e.preventDefault();
  if (is90DegreeMode.value) return; // 90° mode is driven by mouseup clicks, not drag
  const raw = toOverlayPct(e);
  if (raw) drawStart.value = snapToEndpoints(raw);
};

const onDrawMove = (e: MouseEvent): void => {
  if (!drawStart.value) return;
  const raw = toOverlayPct(e);
  if (!raw) return;
  if (is90DegreeMode.value && orthoStep.value === 2) {
    // Preview segment B→C forced perpendicular to A→B
    const snapped = snapToEndpoints(raw);
    drawCurrent.value = snapTarget.value ? snapped : getOrthoPerpPoint(raw);
    return;
  }
  const snapped = snapToEndpoints(raw);
  drawCurrent.value = snapTarget.value
    ? snapped
    : applyAngleSnap(drawStart.value, snapped);
};

const onDrawEnd = (e: MouseEvent): void => {
  if (!drawStart.value) return;
  const raw = toOverlayPct(e);
  if (!raw) return;
  const snapped = snapToEndpoints(raw);
  const finalPt = snapTarget.value
    ? snapped
    : applyAngleSnap(drawStart.value, snapped);
  const dx = finalPt.x - drawStart.value.x;
  const dy = finalPt.y - drawStart.value.y;
  if (Math.sqrt(dx * dx + dy * dy) > 0.5) {
    const id = crypto.randomUUID();
    segments.value.push({
      id,
      from: { ...drawStart.value },
      to: finalPt,
      color: newLineColor.value,
      style: newLineStyle.value,
      hasArrow: newLineHasArrow.value,
    });
    animateSegment(id);
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// Zoom / Pan
// ─────────────────────────────────────────────────────────────────────────────
const MIN_SCALE = ref(0.3);
const MAX_SCALE = 5;
const ZOOM_STEP = 0.1;
const ZOOM_BTN_STEP = 0.2;
/** 3 % breathing room so the image never grazes the container edge. */
const PADDING_FACTOR = 0.97;

const containerRef = ref<HTMLElement | null>(null);
const overlayRef = ref<HTMLElement | null>(null);

const scale = ref(MIN_SCALE.value);
const position = ref({ x: 0, y: 0 });
const panStart = ref<{ x: number; y: number } | null>(null);

/** Restored by the reset button. Updated once the image loads. */
const defaultState = ref({ scale: MIN_SCALE.value, position: { x: 0, y: 0 } });

// ─── Rubber-band selection state ─────────────────────────────────────────────
/** Screen-space start point of an in-progress selection drag (left mouse on empty canvas). */
const selectionStart = ref<{ clientX: number; clientY: number } | null>(null);
/** Tracks current mouse position during selection drag — updated via document-level listener. */
const selectionCurrent = ref<{ clientX: number; clientY: number } | null>(null);
/** Prevents onCanvasClick from deselecting immediately after a selection is committed. */
const justFinishedSelection = ref(false);
/** True while Space is held — overrides selection mode with pan. */
const isSpaceHeld = ref(false);

/** Container-relative rect for the selection overlay div — null when too small to render. */
const selectionBoxRect = computed(() => {
  if (!selectionStart.value || !selectionCurrent.value || !containerRef.value) return null;
  const r = containerRef.value.getBoundingClientRect();
  const x1 = selectionStart.value.clientX - r.left;
  const y1 = selectionStart.value.clientY - r.top;
  const x2 = selectionCurrent.value.clientX - r.left;
  const y2 = selectionCurrent.value.clientY - r.top;
  if (Math.abs(x2 - x1) < 4 && Math.abs(y2 - y1) < 4) return null;
  return {
    left: Math.min(x1, x2),
    top: Math.min(y1, y2),
    width: Math.abs(x2 - x1),
    height: Math.abs(y2 - y1),
  };
});

// Document-level handlers for selection drag — guarantees events are received even
// when the pointer drifts over overlay items (which own their own pointer-events).
const onSelectionMove = (e: MouseEvent): void => {
  selectionCurrent.value = { clientX: e.clientX, clientY: e.clientY };
};

const cleanupSelectionListeners = (): void => {
  document.removeEventListener('mousemove', onSelectionMove);
  document.removeEventListener('mouseup', onSelectionEnd);
};

const onSelectionEnd = (): void => {
  const box = selectionBoxRect.value;
  if (box && overlayRef.value) {
    const overlayRect = overlayRef.value.getBoundingClientRect();
    const sx1 = Math.min(selectionStart.value!.clientX, selectionCurrent.value!.clientX);
    const sy1 = Math.min(selectionStart.value!.clientY, selectionCurrent.value!.clientY);
    const sx2 = Math.max(selectionStart.value!.clientX, selectionCurrent.value!.clientX);
    const sy2 = Math.max(selectionStart.value!.clientY, selectionCurrent.value!.clientY);
    const hit = props.overlay.filter((item) => {
      const sx = overlayRect.left + (item.x / 100) * overlayRect.width;
      const sy = overlayRect.top + (item.y / 100) * overlayRect.height;
      return sx >= sx1 && sx <= sx2 && sy >= sy1 && sy <= sy2;
    });
    if (hit.length > 0) {
      emit('selectMultiple', hit.map((i) => i.id));
      // Flag consumed by the very next onCanvasClick (the click that follows this mouseup).
      // Do NOT use nextTick here — the microtask queue drains before the click macrotask,
      // which would reset the flag too early and let onCanvasClick deselect immediately.
      justFinishedSelection.value = true;
    }
  }
  cleanupSelectionListeners();
  selectionStart.value = null;
  selectionCurrent.value = null;
};

/**
 * Computes the scale + translate that makes the full image visible inside the
 * container, with a small padding margin, and centres it.
 *
 * Uses transformOrigin: top left so the scale origin is always on-screen and
 * the position arithmetic stays predictable.
 *
 * Called both from @load (normal load) and onMounted (cached image).
 */
const computeFit = async (): Promise<void> => {
  await nextTick(); // let the browser finish layout before measuring
  const container = containerRef.value;
  const img = container?.querySelector<HTMLImageElement>('.base-svg');
  if (!container || !img || !img.naturalWidth || !img.naturalHeight) return;

  const containerW = container.clientWidth;
  const containerH = container.clientHeight;
  if (!containerW || !containerH) return;

  // Height the image would occupy at scale = 1 (CSS width:100%)
  const renderedH = containerW * (img.naturalHeight / img.naturalWidth);

  // Scale so the tallest dimension fits, then pull back slightly for padding
  const fitScale = Math.min(1, containerH / renderedH) * PADDING_FACTOR;

  // With transformOrigin: top left, the top-left corner maps to (s*tx, s*ty).
  // Centre the image both horizontally and vertically.
  const fitX = (containerW * (1 - fitScale)) / 2 / fitScale;
  const fitY = Math.max(0, (containerH - renderedH * fitScale) / 2 / fitScale);

  MIN_SCALE.value = fitScale;
  defaultState.value = { scale: fitScale, position: { x: fitX, y: fitY } };
  scale.value = fitScale;
  position.value = { x: fitX, y: fitY };
};

const onImageLoad = (): void => {
  computeFit();
};

let resizeObserver: ResizeObserver | null = null;

const onKeyDown = (e: KeyboardEvent): void => {
  if (e.key === ' ') {
    const t = e.target as HTMLElement;
    if (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable) return;
    isSpaceHeld.value = true;
    e.preventDefault(); // suppress page scroll
    return;
  }
  if (e.key === 'Escape') {
    isDrawingMode.value = false;
    resetOrthoState();
    if (selectionStart.value) {
      cleanupSelectionListeners();
      selectionStart.value = null;
      selectionCurrent.value = null;
    }
  }
  if (
    !props.readonly &&
    (e.key === 'Delete' || e.key === 'Backspace') &&
    selectedSegmentId.value
  ) {
    deleteSelectedSegment();
  }
};

const onKeyUp = (e: KeyboardEvent): void => {
  if (e.key === ' ') isSpaceHeld.value = false;
};

watch(
  () => props.readonly,
  (isReadonly) => {
    if (isReadonly) {
      isDrawingMode.value = false;
      resetOrthoState();
      selectedSegmentId.value = null;
      stopHintRotation();
    } else {
      startHintRotation();
    }
  }
);

watch(segments, (segs) => emit('segmentsChange', segs), { deep: true });

onMounted(() => {
  computeFit();
  if (overlayRef.value) {
    const update = () => {
      svgW.value = overlayRef.value?.clientWidth ?? 0;
      svgH.value = overlayRef.value?.clientHeight ?? 0;
    };
    update();
    resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(overlayRef.value);
  }
  window.addEventListener('keydown', onKeyDown);
  window.addEventListener('keyup', onKeyUp);
  if (!props.readonly) startHintRotation();
});

onUnmounted(() => {
  resizeObserver?.disconnect();
  window.removeEventListener('keydown', onKeyDown);
  window.removeEventListener('keyup', onKeyUp);
  cleanupSelectionListeners();
  stopHintRotation();
});

const contentStyle = computed(() => ({
  transform: `scale(${scale.value}) translate(${position.value.x}px, ${position.value.y}px)`,
  transformOrigin: 'top left',
}));

const canReset = computed(
  () =>
    scale.value !== defaultState.value.scale ||
    position.value.x !== defaultState.value.position.x ||
    position.value.y !== defaultState.value.position.y
);

const clampScale = (v: number) =>
  Math.max(MIN_SCALE.value, Math.min(MAX_SCALE, v));

const onWheel = (e: WheelEvent): void => {
  scale.value = clampScale(scale.value + (e.deltaY > 0 ? -1 : 1) * ZOOM_STEP);
};

const onMouseDown = (e: MouseEvent): void => {
  if ((e.target as HTMLElement).closest('.overlay-item')) return;
  if (isDrawingMode.value) return;
  e.preventDefault();

  if (e.button === 1) {
    // Middle mouse → pan (always)
    panStart.value = { x: e.clientX - position.value.x, y: e.clientY - position.value.y };
    return;
  }

  if (e.button === 0) {
    const shouldPan = props.readonly || isSpaceHeld.value;
    if (shouldPan) {
      // View mode or Space held → pan
      panStart.value = { x: e.clientX - position.value.x, y: e.clientY - position.value.y };
    } else {
      // Edit mode without Space → rubber-band selection via document-level listeners
      selectionStart.value = { clientX: e.clientX, clientY: e.clientY };
      selectionCurrent.value = { clientX: e.clientX, clientY: e.clientY };
      document.addEventListener('mousemove', onSelectionMove);
      document.addEventListener('mouseup', onSelectionEnd);
    }
  }
};

/** Deselect any selected item or segment when clicking the blank canvas. */
const onCanvasClick = (e: MouseEvent): void => {
  if (justFinishedSelection.value) {
    // Consume the one-shot guard set by onSelectionEnd and bail.
    // Resetting here (not via nextTick) ensures subsequent clicks still deselect normally.
    justFinishedSelection.value = false;
    return;
  }
  const t = e.target as HTMLElement;
  if (!t.closest('.overlay-item') && !t.closest('.connection-line')) {
    emit('select', null, false);
    selectedSegmentId.value = null;
  }
};

const onMouseMove = (e: MouseEvent): void => {
  if (draggingEndpoint.value) {
    onEndpointDrag(e);
    return;
  }
  if (draggingSegment.value) {
    onSegmentDrag(e);
    return;
  }
  if (drawStart.value !== null) {
    onDrawMove(e);
    return;
  }
  if (!panStart.value) return;
  position.value = {
    x: e.clientX - panStart.value.x,
    y: e.clientY - panStart.value.y,
  };
};

const onMouseUp = (e: MouseEvent): void => {
  if (draggingEndpoint.value) {
    onEndpointDragEnd(e);
    return;
  }
  if (draggingSegment.value) {
    draggingSegment.value = null;
    return;
  }
  if (!props.readonly && isDrawingMode.value && is90DegreeMode.value) {
    handleOrthoClick(e);
    return;
  }
  if (!props.readonly && drawStart.value !== null) {
    onDrawEnd(e);
    drawStart.value = null;
    drawCurrent.value = null;
    snapTarget.value = null;
    return;
  }
  panStart.value = null;
};

const onMouseLeave = (): void => {
  if (draggingEndpoint.value) {
    draggingEndpoint.value = null;
    snapTarget.value = null;
    return;
  }
  if (draggingSegment.value) {
    draggingSegment.value = null;
    return;
  }
  // Keep ortho state alive on leave so the user can re-enter and continue
  if (!is90DegreeMode.value) {
    drawStart.value = null;
    drawCurrent.value = null;
  }
  drawCurrent.value = null;
  snapTarget.value = null;
  panStart.value = null;
};

const zoomIn = () => {
  scale.value = clampScale(scale.value + ZOOM_BTN_STEP);
};
const zoomOut = () => {
  scale.value = clampScale(scale.value - ZOOM_BTN_STEP);
};
const reset = () => {
  scale.value = defaultState.value.scale;
  position.value = { ...defaultState.value.position };
};

// ─────────────────────────────────────────────────────────────────────────────
// Snap-to-alignment
// ─────────────────────────────────────────────────────────────────────────────
/** Max distance in % units within which an item snaps to an alignment axis. */
const SNAP_THRESHOLD = 1.5;

const snapGuides = ref<{ x: number | null; y: number | null }>({
  x: null,
  y: null,
});

/**
 * Given raw % coordinates for an item being dragged, finds the nearest
 * alignment axis among all other overlay items and returns snapped coords.
 * Sets `snapGuides` as a side effect so the template can render guide lines.
 */
const applySnap = (
  rawX: number,
  rawY: number,
  excludeId?: string
): { x: number; y: number } => {
  const others = props.overlay.filter((i) => i.id !== excludeId);
  let snappedX = rawX;
  let snappedY = rawY;
  let guideX: number | null = null;
  let guideY: number | null = null;
  let minDistX = SNAP_THRESHOLD;
  let minDistY = SNAP_THRESHOLD;

  for (const other of others) {
    const dx = Math.abs(other.x - rawX);
    const dy = Math.abs(other.y - rawY);
    if (dx < minDistX) {
      minDistX = dx;
      snappedX = other.x;
      guideX = other.x;
    }
    if (dy < minDistY) {
      minDistY = dy;
      snappedY = other.y;
      guideY = other.y;
    }
  }

  snapGuides.value = { x: guideX, y: guideY };
  return { x: snappedX, y: snappedY };
};

// ─────────────────────────────────────────────────────────────────────────────
// Drag-and-drop
// ─────────────────────────────────────────────────────────────────────────────
const isDraggingOver = ref(false);

const onDragOver = (): void => {
  isDraggingOver.value = true;
};

const onDragLeave = (e: DragEvent): void => {
  // Only clear when the pointer actually leaves the container
  if (!containerRef.value?.contains(e.relatedTarget as Node)) {
    isDraggingOver.value = false;
  }
};

const onDrop = (e: DragEvent): void => {
  isDraggingOver.value = false;

  if (!e.dataTransfer || !overlayRef.value) return;

  const raw = e.dataTransfer.getData('application/json');
  if (!raw) return;

  const parsed = JSON.parse(raw) as {
    equipment: IEquipment;
    siteSystemId?: number;
    siteSystemName?: string;
    readings?: IEquipmentReading[];
  };
  const equip = parsed.equipment;

  // Compute position as a percentage of the overlay layer's visual bounds.
  const rect = overlayRef.value.getBoundingClientRect();
  const rawX = Math.max(
    0,
    Math.min(100, ((e.clientX - rect.left) / rect.width) * 100)
  );
  const rawY = Math.max(
    0,
    Math.min(100, ((e.clientY - rect.top) / rect.height) * 100)
  );

  const { x, y } = applySnap(rawX, rawY);
  snapGuides.value = { x: null, y: null };

  emit('drop', {
    equipment: equip,
    siteSystemId: parsed.siteSystemId,
    siteSystemName: parsed.siteSystemName,
    readings: parsed.readings,
    x,
    y,
  });
};

const onItemMove = (p: { id: string; x: number; y: number }): void => {
  const { x, y } = applySnap(p.x, p.y, p.id);
  emit('move', p.id, x, y);
};

const onItemMoveEnd = (): void => {
  snapGuides.value = { x: null, y: null };
};

// ─────────────────────────────────────────────────────────────────────────────
// Contextual hint system
// ─────────────────────────────────────────────────────────────────────────────
const ROTATING_HINTS = [
  'Drag equipment from the palette onto the canvas to place it',
  'Left-drag on empty canvas to rubber-band select multiple items',
  'Hold Space + left-drag to pan · scroll wheel to zoom',
  'Click an item to select it · Ctrl+click to add to selection',
  'Connect equipment by drawing lines with the pencil tool (top-left)',
  'Press Delete to remove selected items',
  'Cmd+Z / Cmd+Shift+Z to undo and redo',
  'Middle-mouse drag is another way to pan the canvas',
];

const hintIndex = ref(0);
const hintsEnabled = ref(true);
let hintTimer: ReturnType<typeof setInterval> | null = null;

const contextualHint = computed((): string | null => {
  if (props.readonly) return null;

  // Active rubber-band drag
  if (selectionStart.value) return 'Release to select all items inside the rectangle';

  // Drawing mode states
  if (isDrawingMode.value) {
    if (is90DegreeMode.value) {
      if (orthoStep.value === 1) return 'Click to place the elbow point';
      if (orthoStep.value === 2) return 'Click to complete the L-shape · Esc to cancel';
    }
    if (drawStart.value) return 'Release to place the connection line';
    return 'Click-drag to draw a connection line · Esc to exit';
  }

  // Segment selected
  if (selectedSegmentId.value) return 'Pick a color · toggle arrow · Del to remove this line';

  // Items selected
  const selCount = (props.selectedIds ?? []).length;
  if (selCount > 1) return `${selCount} items selected — press Delete to remove them`;
  if (selCount === 1) return 'Tip: drag a rectangle on empty canvas to multi-select';

  return null;
});

const currentHint = computed((): string | null => {
  if (props.readonly) return null;
  return contextualHint.value ?? ROTATING_HINTS[hintIndex.value];
});

const startHintRotation = (): void => {
  hintTimer = setInterval(() => {
    // Only advance the index when no contextual hint is active
    if (!contextualHint.value) {
      hintIndex.value = (hintIndex.value + 1) % ROTATING_HINTS.length;
    }
  }, 10000);
};

const stopHintRotation = (): void => {
  if (hintTimer !== null) {
    clearInterval(hintTimer);
    hintTimer = null;
  }
};
</script>

<style lang="scss" scoped>
.canvas-container {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  // View-mode default: grab (panning is the primary action)
  cursor: grab;
  background: #fff;

  &:active {
    cursor: grabbing;
  }

  // Edit mode: crosshair signals rubber-band selection is the primary action
  &.is-edit-mode {
    cursor: crosshair;
    &:active { cursor: crosshair; }
  }

  // Space held in edit mode: switch back to grab for panning
  &.is-space-pan {
    cursor: grab !important;
    &:active { cursor: grabbing !important; }
  }

  &.is-drawing {
    cursor: crosshair;
    &:active { cursor: crosshair; }
  }

  &.is-endpoint-drag {
    cursor: grabbing !important;
    &:active { cursor: grabbing !important; }
  }

  &.is-segment-drag {
    cursor: grabbing !important;
    &:active { cursor: grabbing !important; }
  }
}

.canvas-content {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  transition: transform 0.12s ease;
}

.base-svg {
  display: block;
  width: 100%;
  height: auto;
}

.overlay-layer {
  position: absolute;
  inset: 0;
  pointer-events: none;

  :deep(.overlay-item),
  .connection-color-picker {
    pointer-events: all;
  }
}

.canvas-controls {
  position: absolute;
  bottom: 16px;
  right: 16px;
  display: flex;
  flex-direction: row;
  gap: 4px;
  z-index: 20;
}

.drop-hint {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(55, 140, 218, 0.08);
  border: 2px dashed #378cda;
  color: #378cda;
  font-size: 1rem;
  font-weight: 600;
  pointer-events: none;
  z-index: 30;
}

// Fade transition for the drop-hint
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.connections-svg {
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
  z-index: 5;
  overflow: visible;

  &.draw-mode {
    pointer-events: all;
    cursor: crosshair;
  }
}

.connection-line {
  pointer-events: stroke;
  cursor: grab;

  &--animating {
    stroke-dashoffset: 1;
    animation: dt-draw-connection 0.75s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  }

  &--selected {
    filter: drop-shadow(0 0 4px rgba(0, 0, 0, 0.3));
  }
}

@keyframes dt-draw-connection {
  from {
    stroke-dashoffset: 1;
  }
  to {
    stroke-dashoffset: 0;
  }
}

// Floating color picker shown when a connection line is selected
.connection-color-picker {
  position: absolute;
  transform: translate(-50%, -50%);
  display: flex;
  gap: 5px;
  align-items: center;
  background: white;
  border-radius: 20px;
  padding: 5px 8px;
  box-shadow: 0 3px 14px rgba(0, 0, 0, 0.22);
  border: 1px solid rgba(0, 0, 0, 0.1);
  z-index: 25;

  .color-swatch {
    width: 28px;
    height: 28px;
  }
}

.color-swatch {
  width: 100%;
  aspect-ratio: 1;
  border-radius: 6px;
  border: 2px solid transparent;
  cursor: pointer;
  transition:
    transform 0.12s ease,
    border-color 0.12s ease;
  outline: none;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &:hover {
    transform: scale(1.1);
  }

  &--active {
    border-color: rgba(0, 0, 0, 0.5);
    transform: scale(1.05);
  }
}

.segment-delete-btn {
  background: #b71c1c !important;
}

.arrow-toggle-btn {
  background: #455a64 !important;
}

.endpoint-handle {
  cursor: grab;
  pointer-events: all;

  &:hover {
    filter: drop-shadow(0 0 3px rgba(0, 0, 0, 0.35));
  }

  &:active {
    cursor: grabbing;
  }
}

.line-toolbox {
  position: absolute;
  top: 16px;
  left: 16px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 3px;
  z-index: 20;
  background: white;
  border-radius: 12px;
  padding: 6px;
  box-shadow: 0 3px 14px rgba(0, 0, 0, 0.18);
  border: 1px solid rgba(0, 0, 0, 0.08);
  width: 72px;
}

.toolbox-btn {
  width: 100% !important;
  height: 40px !important;
  border-radius: 8px !important;

  :deep(.v-btn__overlay),
  :deep(.v-btn__underlay) {
    border-radius: 8px;
  }
}

.toolbox-divider {
  width: 100%;
  margin: 2px 0;
  opacity: 0.2;
}

.color-swatch-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 5px;
}

// Snap-to-alignment guide lines
.snap-guide {
  position: absolute;
  pointer-events: none;
  z-index: 15;

  &--vertical {
    top: 0;
    bottom: 0;
    width: 1px;
    background: rgba(255, 152, 0, 0.9);
    transform: translateX(-50%);
  }

  &--horizontal {
    left: 0;
    right: 0;
    height: 1px;
    background: rgba(255, 152, 0, 0.9);
    transform: translateY(-50%);
  }
}

// Hint box container — bottom-center, above zoom controls
.hint-container {
  position: absolute;
  bottom: 56px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 25;
  pointer-events: none; // children opt-in via pointer-events: all
  display: flex;
  justify-content: center;
}

// Rubber-band selection box
.selection-box {
  position: absolute;
  border: 1.5px dashed #1565c0;
  background: rgba(21, 101, 192, 0.08);
  pointer-events: none;
  z-index: 40;
  border-radius: 2px;
}

.canvas-container.is-selecting {
  cursor: crosshair;
}
</style>
